import { HIRING_RELIABILITY_CONFIG } from "@clipboard-health/contract-backend-main";
import { Stack } from "@mui/material";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { Button } from "@src/appV2/redesign/components/Button";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { IconButton } from "@src/appV2/redesign/components/IconButton";
import {
  BottomSheet,
  Illustration,
  Text,
  TextButton,
  type UseModalState,
} from "@src/appV2/redesign/system/ui-components";
import { Link } from "react-router-dom";

import {
  INTERVIEW_TYPE,
  type InterviewType,
} from "../../WorkerShifts/api/useFetchPaginatedInterviews";

interface InterviewTodayRescheduleBottomSheetProps {
  modalState: UseModalState;
  interviewId: string;
  attendanceScore: number;
  interviewType: InterviewType;
  onCancel: () => void;
  onReschedule: () => void;
}

export function InterviewTodayRescheduleBottomSheet(
  props: InterviewTodayRescheduleBottomSheetProps
) {
  const { modalState, onCancel, onReschedule, interviewId, attendanceScore, interviewType } = props;

  const isPhoneScreen = interviewType === INTERVIEW_TYPE.PHONE_SCREEN;
  const rescheduleLabel = isPhoneScreen ? "phone call" : "interview";
  const hasAttendanceCredits = attendanceScore > 0;
  const restrictedDays = HIRING_RELIABILITY_CONFIG.RESTRICTED_DAYS;

  const getDescription = () => {
    if (isPhoneScreen) {
      return `It's too late to reschedule your ${rescheduleLabel} for another day. You can still reschedule for today.`;
    }

    if (hasAttendanceCredits) {
      return `It's too late to reschedule your interview for another day. However, since you have a strong interview attendance record — we're going to give you a free pass on this one. Your account will not be restricted. If you cancel past 8am or no-show in the future, you'll be restricted from viewing jobs for ${restrictedDays} days.`;
    }

    return `It's too late to reschedule your interview for another day. If you cancel or no-show, you'll be restricted from viewing job openings for ${restrictedDays} days.`;
  };

  useLogEffect(APP_V2_APP_EVENTS.PLACEMENT_INTERVIEW_TODAY_RESCHEDULE_MODAL_VIEWED, {
    interviewId,
    attendanceScore,
  });

  return (
    <BottomSheet
      modalState={modalState}
      footer={
        <DialogFooter orientation="vertical">
          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={() => {
              logEvent(APP_V2_APP_EVENTS.PLACEMENT_INTERVIEW_TODAY_RESCHEDULE_MODAL_ACTIONS, {
                interviewId,
                action: "reschedule",
              });
              onReschedule();
            }}
          >
            Reschedule for today {isPhoneScreen ? "" : "(no penalty)"}
          </Button>
          <TextButton
            LinkComponent={Link}
            size="large"
            sx={{ mt: 1, mb: 3 }}
            onClick={() => {
              logEvent(APP_V2_APP_EVENTS.PLACEMENT_INTERVIEW_TODAY_RESCHEDULE_MODAL_ACTIONS, {
                interviewId,
                action: "cancel",
              });
              onCancel();
            }}
          >
            Cancel
          </TextButton>
        </DialogFooter>
      }
    >
      <IconButton
        size="small"
        iconType="close"
        variant="outlined"
        sx={{ position: "absolute", top: 24, right: 16 }}
        onClick={modalState.closeModal}
      />
      <Stack gap={7} px={7} pb={8} pt="76px" alignItems="center" justifyContent="center">
        <Illustration type="calendar-alert" />
        <Text variant="h2" textAlign="center">
          Reschedule your {rescheduleLabel}?
        </Text>
        <Text textAlign="center" color={(theme) => theme.palette.text.secondary}>
          {getDescription()}
        </Text>
      </Stack>
    </BottomSheet>
  );
}
